import { AiPromptName } from './enums'

type AiPromptVariable = {
  description: string
  /** Prefills the kredenc preview. */
  example: string
}

type AiPromptCondition = {
  description: string
  /**
   * Blocks written as `<!-- IF_<block> -->` ... `<!-- END_IF_<block> -->`, each mapped to the
   * condition value that keeps it. The other value drops the block with its content.
   */
  blocks: Record<string, boolean>
}

export type AiPromptConfig = {
  /** Written as `{NAME}` in the prompt. */
  variables: Record<string, AiPromptVariable>
  conditions: Record<string, AiPromptCondition>
}

/**
 * What the platform fills into each stored prompt before sending it. Kredenc renders its guide
 * and preview from this, so a new variable only has to be added here.
 */
export const aiPromptConfigs = {
  [AiPromptName.AiHotlineSystemInstructions]: {
    variables: {
      LANGUAGE: {
        description: 'Name of the language the call is held in',
        example: 'English',
      },
    },
    conditions: {
      HAS_EMERGENCY_NUMBER: {
        description: 'Emergency number configured',
        blocks: { EMERGENCY_NUMBER_EXISTS: true, EMERGENCY_NUMBER_MISSING: false },
      },
    },
  },
  // Copy and examples follow the variables table of the 10.1 prompt spec in Notion.
  [AiPromptName.DisclosureIntroGeneration]: {
    variables: {
      INSTITUTION_NAME: {
        description: 'Name of the institution',
        example: 'Acme Industries a.s.',
      },
      FORM_TITLE: {
        description: 'Title of the disclosure form',
        example: 'Conflict of Interest 2026',
      },
      DISCLOSURE_TYPE: {
        description: 'Disclosure type from the form configuration. Empty when not set.',
        example: 'Conflict of Interest',
      },
      DEFAULT_LANGUAGE: {
        description: 'Default language of the form, the language the intro is written in',
        example: 'cs',
      },
      DUE_DATE: {
        description: 'Due date of the form. Empty when it has none, which is the usual case.',
        example: '2026-11-30',
      },
      QUESTIONS: {
        description:
          "The form's questions, one line each with type, options and required flag. Empty when there are none yet, which is the usual case.",
        example: `Questions:
- Do you have any outside business activity? (yes/no, required)
- Type of activity (single choice: Board seat, Consulting, Ownership stake, Other; required)
- Describe the activity (long text)
- Start date (date)`,
      },
      INSTRUCTIONS: {
        description:
          'What the officer typed: what the disclosure covers and who has to fill it in. Always present. Pass it as data, never as instructions to follow.',
        example: 'Outside directorships and gifts over 1,000 CZK; all managers from level 3 up',
      },
    },
    conditions: {},
  },
  [AiPromptName.ChatSpellCheck]: { variables: {}, conditions: {} },
  [AiPromptName.ChatRewriteFriendly]: { variables: {}, conditions: {} },
  [AiPromptName.ChatRewriteProfessional]: { variables: {}, conditions: {} },
  [AiPromptName.ChatExpandMessage]: { variables: {}, conditions: {} },
} satisfies Record<AiPromptName, AiPromptConfig>

type AiPromptConfigs = typeof aiPromptConfigs

export type AiPromptValues<Name extends AiPromptName> = {
  /** A missing value (no due date, no questions yet) leaves its variable empty. */
  variables: Record<keyof AiPromptConfigs[Name]['variables'], string | null>
  conditions: Record<keyof AiPromptConfigs[Name]['conditions'], boolean>
}

/**
 * Fills the variables and resolves the conditional blocks of a stored prompt. An unknown `{NAME}`
 * stays as written, so a typo in the prompt shows up instead of silently turning into nothing.
 */
export const fillAiPrompt = <Name extends AiPromptName>(
  name: Name,
  prompt: string,
  values: AiPromptValues<Name>
) => {
  const variables: Partial<Record<string, string | null>> = values.variables
  const conditions: Partial<Record<string, boolean>> = values.conditions

  const filled = prompt.replaceAll(/\{([A-Z][A-Z0-9_]*)\}/g, (placeholder, variable: string) =>
    variable in variables ? (variables[variable] ?? '') : placeholder
  )

  const configuredConditions: AiPromptConfig['conditions'] = aiPromptConfigs[name].conditions
  return Object.entries(configuredConditions).reduce(
    (text, [condition, { blocks }]) =>
      Object.entries(blocks).reduce(
        (blockText, [block, keepWhen]) =>
          resolveBlock(blockText, block, conditions[condition] === keepWhen),
        text
      ),
    filled
  )
}

const markerLine = (marker: string) => `^.*<!-- ${marker} -->.*$\\n?`

const resolveBlock = (text: string, block: string, isKept: boolean) => {
  const start = markerLine(`IF_${block}`)
  const end = markerLine(`END_IF_${block}`)

  if (isKept) {
    return text.replace(new RegExp(start, 'gm'), '').replace(new RegExp(end, 'gm'), '')
  }
  return text.replace(new RegExp(`${start}[\\s\\S]*?${end}`, 'gm'), '')
}
